import { apiFetch } from "@/lib/api";

export interface ShipmentDetailLine {
  batch_item_id: number;
  sku: string;
  quantity: number;
  build_order_id: number | null;
  build_order_number: string | null;
  quantity_assembled: number;
  sales_order_id: number | null;
  sales_order_number: string | null;
  sgs_ship_by_date: string | null;
  customer_purchase_order_number: string | null;
}

export interface ShipmentDetailSalesOrder {
  sales_order_id: number;
  sales_order_number: string | null;
  customer_purchase_order_number: string | null;
  sgs_ship_by_date: string | null;
  client_name: string | null;
}

export interface ShipmentDetailSiblingShipment {
  shipment_id: number;
  sent_to_pack_out: boolean;
  date_shipped: string | null;
  ready_for_pickup: boolean | null;
}

export interface ShipmentBrowseListRow {
  shipment_id: number;
  batch_id: number | null;
  date_shipped: string | null;
  date_created: string | null;
  carrier: string | null;
  tracking: string | null;
  sent_to_pack_out: boolean;
  ready_for_pickup: boolean | null;
  batch_mfr_ship_by_date: string | null;
  batch_location_id: number | null;
  batch_location_label: string;
  sku_summary: string | null;
  sales_order_summary: string | null;
}

export interface ShipmentSerialRow {
  serial_fci_id: number;
  device_label_serial: string | null;
  finished_sku: string | null;
  batch_line_sku: string | null;
  box_serial: string | null;
  imei: string | null;
  carton_number: number | null;
  wireless_portal_org_name: string | null;
  wireless_portal_group_names: string | null;
}

export interface ShipmentDetail {
  shipment_id: number;
  batch_id: number;
  date_created: string | null;
  date_shipped: string | null;
  carrier: string | null;
  tracking: string | null;
  sent_to_pack_out: boolean;
  sent_to_pack_out_at: string | null;
  date_sent_to_pack_out: string | null;
  ready_for_pickup: boolean | null;
  ready_for_pickup_at: string | null;
  note: string | null;
  serial_count: number;
  batch_mfr_ship_by_date: string | null;
  batch_ship_to_recipient: string | null;
  batch_ship_to_address_line_1: string | null;
  batch_ship_to_address_line_2: string | null;
  batch_ship_to_address_line_3: string | null;
  batch_ship_to_city: string | null;
  batch_ship_to_state: string | null;
  batch_ship_to_zip: string | null;
  batch_ship_to_country: string | null;
  batch_manufacturer_sales_order_number: string | null;
  batch_location_id: number | null;
  batch_location_label: string;
  batch_shipment_on_hold: boolean;
  lines: ShipmentDetailLine[];
  sales_orders: ShipmentDetailSalesOrder[];
  batch_shipments: ShipmentDetailSiblingShipment[];
}

export async function fetchShipmentBrowseList(query: string, limit = 100): Promise<ShipmentBrowseListRow[]> {
  const data = await apiFetch<{ rows: ShipmentBrowseListRow[] }>("/api/shipping/shipments/browse", {
    query: { q: query.trim(), limit: String(limit) },
  });
  return data.rows;
}

export async function fetchShipmentDetail(shipmentId: number): Promise<ShipmentDetail> {
  const data = await apiFetch<{ shipment: ShipmentDetail }>(`/api/shipping/shipments/${String(shipmentId)}/detail`);
  return data.shipment;
}

export async function fetchShipmentSerials(shipmentId: number): Promise<ShipmentSerialRow[]> {
  const data = await apiFetch<{ rows: ShipmentSerialRow[] }>(`/api/shipping/shipments/${String(shipmentId)}/serials`);
  return data.rows;
}

export async function fetchShipmentSerialsPortal(
  shipmentId: number,
): Promise<
  Record<
    string,
    {
      wireless_portal_org_name: string | null;
      wireless_portal_group_names: string | null;
    }
  >
> {
  const data = await apiFetch<{
    by_imei: Record<
      string,
      {
        wireless_portal_org_name: string | null;
        wireless_portal_group_names: string | null;
      }
    >;
  }>(`/api/shipping/shipments/${String(shipmentId)}/serials/portal`);
  return data.by_imei;
}

export function filterShipmentBrowseRowsClient(rows: ShipmentBrowseListRow[], searchText: string): ShipmentBrowseListRow[] {
  const trimmed = searchText.trim().toLowerCase();
  if (trimmed === "") {
    return rows;
  }
  return rows.filter((row) => {
    const haystack = [
      String(row.shipment_id),
      row.batch_id != null ? String(row.batch_id) : null,
      row.carrier,
      row.tracking,
      row.sku_summary,
      row.sales_order_summary,
    ]
      .filter((piece) => piece != null && String(piece).trim() !== "")
      .join(" ")
      .toLowerCase();
    return haystack.includes(trimmed);
  });
}
